import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ArrowUpRight, Compass } from 'lucide-react';
import { studioData } from '../data/portfolioData';

interface NavbarProps {
  onOpenInquiry?: () => void;
}

export const Navbar: React.FC<NavbarProps> = ({ onOpenInquiry }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Selected Works', href: '#works' },
    { name: 'Philosophy', href: '#philosophy' },
    { name: 'Methodology', href: '#methodology' },
    { name: 'Monograph Index', href: '#index' },
    { name: 'Studio', href: '#studio' },
  ];

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-[#0A0B0D]/90 backdrop-blur-md py-3.5 border-b border-white/10 shadow-2xl'
            : 'bg-gradient-to-b from-[#0A0B0D]/80 to-transparent py-6 border-b border-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
          {/* Studio Brand / Monogram */}
          <a href="#" className="group flex items-center gap-3">
            <div className="w-9 h-8 px-1 rounded-sm bg-white/5 border border-white/15 flex items-center justify-center text-arch-accent font-mono text-xs font-semibold group-hover:border-arch-accent transition-colors">
              DG
            </div>
            <div>
              <span className="font-serif text-lg tracking-[0.12em] font-semibold text-white group-hover:text-arch-accent transition-colors">
                {studioData.name}
              </span>
              <span className="block text-[9px] font-mono tracking-widest text-arch-muted uppercase">
                Architecture & Tectonics
              </span>
            </div>
          </a>

          {/* Desktop Navigation Links */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="text-xs tracking-architect uppercase text-neutral-300 hover:text-arch-accent transition-colors relative py-1 group"
              >
                {link.name}
                <span className="absolute bottom-0 left-0 w-0 h-[1px] bg-arch-accent transition-all duration-300 group-hover:w-full" />
              </a>
            ))}
          </nav>

          {/* Action / Availability & CTA */}
          <div className="hidden sm:flex items-center gap-5">
            <div className="hidden xl:flex items-center gap-2 text-[10px] font-mono text-neutral-400 bg-white/5 px-2.5 py-1 rounded-full border border-white/10">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span>Available Q3/Q4</span>
            </div>

            <a
              href="#inquire"
              onClick={onOpenInquiry}
              className="group relative inline-flex items-center gap-2 px-4 py-2 text-xs font-mono tracking-wider uppercase bg-arch-accent/10 border border-arch-accent/40 text-arch-accent hover:bg-arch-accent hover:text-[#0A0B0D] transition-all duration-300 rounded-sm"
            >
              <span>Commission Work</span>
              <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>

          {/* Mobile Menu Toggle Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 text-neutral-300 hover:text-white rounded focus:outline-none"
            aria-label="Toggle Navigation Menu"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </header>

      {/* Mobile Drawer Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-[#0A0B0D]/95 backdrop-blur-xl pt-24 px-6 pb-12 flex flex-col justify-between lg:hidden border-b border-white/10"
          >
            <div className="space-y-6">
              <div className="text-[10px] font-mono text-arch-muted uppercase tracking-widest border-b border-white/10 pb-3 flex items-center justify-between">
                <span>Navigation Index</span>
                <Compass className="w-3.5 h-3.5 text-arch-accent animate-spin" style={{ animationDuration: '12s' }} />
              </div>

              <div className="flex flex-col gap-4">
                {navLinks.map((link, idx) => (
                  <a
                    key={link.name}
                    href={link.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center justify-between text-lg font-serif tracking-wide text-neutral-200 hover:text-arch-accent transition-colors py-2 border-b border-white/5"
                  >
                    <span>{link.name}</span>
                    <span className="text-xs font-mono text-arch-muted">0{idx + 1}</span>
                  </a>
                ))}
              </div>
            </div>

            <div className="space-y-4 pt-6 border-t border-white/10">
              <div className="flex items-center gap-2 text-xs font-mono text-neutral-400">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <span>Accepting 2025/2026 Commissions</span>
              </div>
              <a
                href="#inquire"
                onClick={() => {
                  setMobileMenuOpen(false);
                  if (onOpenInquiry) onOpenInquiry();
                }}
                className="w-full flex items-center justify-center gap-2 py-3.5 text-xs font-mono uppercase tracking-widest bg-arch-accent text-[#0A0B0D] font-semibold rounded-sm"
              >
                <span>Initiate Project Consultation</span>
                <ArrowUpRight className="w-4 h-4" />
              </a>
              <div className="text-center text-[10px] font-mono text-neutral-500">
                {studioData.coordinates}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
